import React, { FormEvent, useState } from 'react';
import { CaretDown, CaretUp, ChatCircleDots, Heart, PencilSimple, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { useCommentStore } from '../../stores/commentStore';
import { useAuthStore } from '../../stores/authStore';
import UserAvatar from '../ui/UserAvatar';
import { formatRelativeDate } from './statusPresentation';
import { submitFormOnEnter } from '../../utils/submitOnEnter';
import { PublicUser } from '../../types';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import HashtagText from '../hashtags/HashtagText';

interface StatusCommentsProps {
  statusId: number;
  commentsCount: number;
  initiallyExpanded?: boolean;
  onOpenUser?: (user: PublicUser) => void;
}

const StatusComments: React.FC<StatusCommentsProps> = ({ statusId, commentsCount, initiallyExpanded = false, onOpenUser }) => {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [message, setMessage] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editMessage, setEditMessage] = useState('');
  const didInitialLoadRef = React.useRef(false);
  const thread = useCommentStore((state) => state.threads[statusId]);
  const liveCount = useCommentStore((state) => state.liveCounts?.[statusId]);
  const fetchComments = useCommentStore((state) => state.fetchComments);
  const createComment = useCommentStore((state) => state.createComment);
  const updateComment = useCommentStore((state) => state.updateComment);
  const likeComment = useCommentStore((state) => state.likeComment);
  const clearError = useCommentStore((state) => state.clearError);
  const currentUserId = useAuthStore((state) => state.user?.id);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated || Boolean(state.user));
  const requireAuth = useRequireAuth();
  const displayedCount = Math.max(commentsCount, liveCount ?? 0);

  React.useEffect(() => {
    if (initiallyExpanded && !didInitialLoadRef.current) {
      didInitialLoadRef.current = true;
      Promise.resolve(fetchComments(statusId)).catch(() => undefined);
    }
  }, [fetchComments, initiallyExpanded, statusId]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !thread?.hasLoaded && !thread?.isLoading) {
      Promise.resolve(fetchComments(statusId)).catch(() => undefined);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isAuthenticated) {
      requireAuth('reply to this status', () => undefined);
      return;
    }
    const value = message.trim();
    if (!value || thread?.isSubmitting) return;
    try {
      await createComment(statusId, value);
      setMessage('');
    } catch {
      // The thread-level error is rendered below.
    }
  };

  const startEditing = (commentId: number, currentMessage: string) => {
    setEditingCommentId(commentId);
    setEditMessage(currentMessage);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditMessage('');
  };

  const submitEdit = async (event: FormEvent, commentId: number) => {
    event.preventDefault();
    const value = editMessage.trim();
    if (!value || thread?.editingIds.has(commentId)) return;
    try {
      await updateComment(statusId, commentId, value);
      cancelEditing();
    } catch {
      // The thread-level error is rendered above.
    }
  };

  return (
    <div className="mt-3 border-t border-slate-500/10 pt-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={`status-comments-${statusId}`}
        className="flex w-full items-center justify-between rounded-xl px-2 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-white/50 dark:text-slate-300 dark:hover:bg-white/[0.06]"
      >
        <span className="inline-flex items-center gap-1.5">
          <ChatCircleDots size={16} weight="duotone" />
          {displayedCount === 0 ? 'Reply' : `${displayedCount} ${displayedCount === 1 ? 'reply' : 'replies'}`}
        </span>
        {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>

      {expanded && (
        <div id={`status-comments-${statusId}`} className="mt-3 space-y-3">
          {thread?.error && (
            <button
              type="button"
              onClick={() => clearError(statusId)}
              className="flex w-full items-center gap-2 rounded-xl bg-pink-500/10 px-3 py-2 text-left text-xs text-pink-700 dark:text-pink-200"
              title="Dismiss error"
            >
              <WarningCircle size={15} /> {thread.error}
            </button>
          )}

          {thread?.hasMore && (
            <button
              type="button"
              disabled={thread.isLoading}
              onClick={() => Promise.resolve(fetchComments(statusId)).catch(() => undefined)}
              className="mx-auto flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-500/10 disabled:opacity-50 dark:text-violet-300"
            >
              {thread.isLoading && <SpinnerGap size={14} className="animate-spin" />}
              Load older replies
            </button>
          )}

          {thread?.items.map((comment) => (
            <div key={comment.id} className="flex gap-2 rounded-xl bg-white/55 p-2.5 dark:bg-white/[0.045]">
              <button type="button" onClick={() => onOpenUser?.(comment.user)} aria-label={`Open ${comment.user.name || 'Map friend'} profile`} className="self-start rounded-xl transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-cyan-400">
                <UserAvatar user={comment.user} size="sm" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <button type="button" onClick={() => onOpenUser?.(comment.user)} className="truncate text-left text-xs font-semibold text-slate-700 transition hover:text-cyan-600 hover:underline dark:text-slate-200 dark:hover:text-cyan-300">
                    {comment.user.name ? <span data-no-i18n>{comment.user.name}</span> : 'Map friend'}
                  </button>
                  <time dateTime={comment.createdAt} className="shrink-0 text-[10px] text-slate-400">
                    {formatRelativeDate(comment.createdAt)}
                  </time>
                </div>
                {editingCommentId === comment.id ? (
                  <form className="mt-2" onSubmit={(event) => submitEdit(event, comment.id)}>
                    <label htmlFor={`edit-reply-${comment.id}`} className="sr-only">Edit reply</label>
                    <textarea
                      id={`edit-reply-${comment.id}`}
                      autoFocus
                      value={editMessage}
                      onChange={(event) => setEditMessage(event.target.value)}
                      maxLength={1000}
                      rows={2}
                      className="w-full resize-y rounded-xl border border-violet-300 bg-white/80 px-3 py-2 text-xs text-slate-800 outline-none focus:ring-2 focus:ring-violet-400/20 dark:border-violet-400/40 dark:bg-white/[0.06] dark:text-white"
                    />
                    <div className="mt-1.5 flex justify-end gap-2">
                      <button type="button" onClick={cancelEditing} className="rounded-lg px-2.5 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-500/10 dark:text-slate-300">
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!editMessage.trim() || thread.editingIds.has(comment.id)}
                        className="rounded-lg bg-violet-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
                      >
                        {thread.editingIds.has(comment.id) ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <p data-no-i18n className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-700 dark:text-slate-200">
                    <HashtagText text={comment.message} />
                  </p>
                )}
                <div className="mt-1 flex items-center gap-1">
                  <button
                    type="button"
                    disabled={thread.likingIds.has(comment.id)}
                    onClick={() => requireAuth('like this reply', () => { void Promise.resolve(likeComment(statusId, comment.id)).catch(() => undefined); })}
                    aria-label={`${comment.isLikedByCurrentUser ? 'Unlike' : 'Like'} reply`}
                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-1 text-[11px] transition disabled:opacity-50 ${comment.isLikedByCurrentUser ? 'text-pink-600 dark:text-pink-300' : 'text-slate-500 hover:text-pink-600 dark:text-slate-400'}`}
                  >
                    <Heart size={13} weight={comment.isLikedByCurrentUser ? 'fill' : 'regular'} />
                    {comment.likesCount || 0}
                  </button>
                  {comment.user.id === currentUserId && editingCommentId !== comment.id && (
                    <button
                      type="button"
                      onClick={() => startEditing(comment.id, comment.message)}
                      aria-label="Edit reply"
                      className="inline-flex items-center gap-1 rounded-full px-1.5 py-1 text-[11px] text-slate-500 transition hover:text-violet-600 dark:text-slate-400 dark:hover:text-violet-300"
                    >
                      <PencilSimple size={13} /> Edit
                    </button>
                  )}
                  {comment.editedAt && (
                    <time
                      dateTime={comment.editedAt}
                      title={`Edited ${new Date(comment.editedAt).toLocaleString()}`}
                      className="ml-auto text-[10px] text-slate-400"
                    >
                      Edited {formatRelativeDate(comment.editedAt)}
                    </time>
                  )}
                </div>
              </div>
            </div>
          ))}

          {thread?.isLoading && !thread.hasLoaded && (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-slate-500">
              <SpinnerGap size={16} className="animate-spin" /> Loading replies…
            </div>
          )}

          <form onSubmit={submit} className="flex items-end gap-2">
            <label htmlFor={`status-reply-${statusId}`} className="sr-only">Reply to status</label>
            <textarea
              id={`status-reply-${statusId}`}
              value={message}
              onFocus={() => {
                if (!isAuthenticated) requireAuth('reply to this status', () => undefined);
              }}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={submitFormOnEnter}
              maxLength={1000}
              rows={1}
              placeholder={isAuthenticated ? 'Write a reply…' : 'Join mapbl to reply…'}
              className="min-h-[38px] flex-1 resize-y rounded-xl border border-slate-200 bg-white/70 px-3 py-2 text-xs text-slate-800 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-white"
            />
            <button
              type="submit"
              disabled={!message.trim() || thread?.isSubmitting}
              className="rounded-xl bg-violet-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {thread?.isSubmitting ? 'Sending…' : 'Reply'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default StatusComments;
